import { memo } from 'react';

import { FileVolumeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { IntegrationIcon } from '@/components/organisms/Integrations';
import { cn } from '@/lib/utils';
import { AuthorType } from '@/shared/types/chat';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { formatTimeOnly } from '@/util/dateUtils';

interface Props {
  created: string | null;
  authorType: string;
  channel?: IntegrationType;
  isVoiceNote?: boolean;
}

const textStyles = 'text-xs leading-4 font-normal text-muted-foreground select-none';

const VOICE_CHANNELS = new Set<IntegrationType>([
  IntegrationType.WEB_VOICE,
  IntegrationType.TWILIO_VOICE,
  IntegrationType.TELNYX_VOICE,
  IntegrationType.AGORA_VOICE,
]);

const MessageStatus = ({ created, authorType, channel, isVoiceNote }: Props) => {
  const { t } = useTranslation();
  const time = created ? formatTimeOnly(created) : '';
  const isVisitor = authorType === AuthorType.VISITOR;
  const transcribed = isVoiceNote || (channel && VOICE_CHANNELS.has(channel));

  return (
    <div
      className={cn(
        'flex items-start self-stretch',
        isVisitor ? 'justify-start gap-2 px-0.5' : 'justify-end gap-2.5 px-5.5'
      )}
    >
      <TimestampTooltip timestamp={created} side="top">
        <span className={cn('cursor-default', textStyles)}>{time}</span>
      </TimestampTooltip>
      {isVisitor && channel && (
        <>
          <span aria-hidden="true" className={textStyles}>
            ·
          </span>
          <span className="flex items-center gap-1">
            <IntegrationIcon type={channel} size={16} className="shrink-0" />
            <span className={textStyles}>{t(getChannelKey(channel))}</span>
          </span>
        </>
      )}
      {isVisitor && transcribed && (
        <>
          <span aria-hidden="true" className={textStyles}>
            ·
          </span>
          <span className="flex items-center gap-1">
            <FileVolumeIcon size={16} className="shrink-0 text-muted-foreground" />
            <span className={textStyles}>{t('conversation.audio_message')}</span>
          </span>
        </>
      )}
    </div>
  );
};

export default memo(MessageStatus);
